export const QUOTE_TEMPLATE_IDS = [
  "modern",
  "two-column-classic",
  "professional-grid",
  "industrial-quotation",
  "steel-distributor",
  "service-quote",
  "foundry-quote",
  "injection-molder",
  "erp-quotation",
] as const;

export type QuoteTemplateId = (typeof QUOTE_TEMPLATE_IDS)[number];

/** Metadata for a quote template including optional required custom fields. */
export interface QuoteTemplateMetadata {
  id: QuoteTemplateId;
  label: string;
  description: string;
  requiredCustomFields?: Array<{
    fieldName: string;
    fieldType: "string" | "number";
    fieldFormat?: string;
  }>;
}

export const DEFAULT_PUBLIC_QUOTE_TEMPLATE: QuoteTemplateId = "modern";

export const QUOTE_TEMPLATE_METADATA: QuoteTemplateMetadata[] = [
  {
    id: "modern",
    label: "Modern (single column)",
    description: "Clean, single-column layout with emphasis on item details.",
  },
  {
    id: "two-column-classic",
    label: "Classic (two column)",
    description: "Traditional layout that separates descriptions and totals.",
  },
  {
    id: "professional-grid",
    label: "Professional (grid)",
    description: "Grid-based layout optimized for large catalogs.",
  },
  {
    id: "industrial-quotation",
    label: "Industrial Quotation",
    description:
      "Table-first quotation format with generic line-item rows and terms.",
  },
  {
    id: "steel-distributor",
    label: "Steel Distributor",
    description:
      "Tabular format with item numbers, weights, and project details — designed for steel and building materials distributors.",
    requiredCustomFields: [
      { fieldName: "Project Name", fieldType: "string" },
      { fieldName: "Contractor", fieldType: "string" },
      { fieldName: "Contact", fieldType: "string" },
      { fieldName: "Escalator Percent 1", fieldType: "number" },
      {
        fieldName: "Escalator Date 1",
        fieldType: "string",
        fieldFormat: "date",
      },
      { fieldName: "Escalator Percent 2", fieldType: "number" },
      {
        fieldName: "Escalator Date 2",
        fieldType: "string",
        fieldFormat: "date",
      },
      { fieldName: "Escalator Percent 3", fieldType: "number" },
      {
        fieldName: "Escalator Date 3",
        fieldType: "string",
        fieldFormat: "date",
      },
      { fieldName: "Escalator Percent 4", fieldType: "number" },
      {
        fieldName: "Escalator Date 4",
        fieldType: "string",
        fieldFormat: "date",
      },
      { fieldName: "Escalator Percent 5", fieldType: "number" },
      {
        fieldName: "Escalator Date 5",
        fieldType: "string",
        fieldFormat: "date",
      },
      { fieldName: "Escalator Percent 6", fieldType: "number" },
      {
        fieldName: "Escalator Date 6",
        fieldType: "string",
        fieldFormat: "date",
      },
    ],
  },
  {
    id: "foundry-quote",
    label: "Foundry Quotation",
    description:
      "Letterhead quotation with a boxed item/spec table for foundries and cast-part manufacturers.",
    requiredCustomFields: [
      { fieldName: "Attention", fieldType: "string" },
      { fieldName: "Your Reference", fieldType: "string" },
      { fieldName: "Copy To", fieldType: "string" },
      { fieldName: "Freight", fieldType: "string" },
      { fieldName: "Sales Taxes", fieldType: "string" },
      { fieldName: "Minimum Order", fieldType: "string" },
    ],
  },
  {
    id: "injection-molder",
    label: "Injection Molder",
    description:
      "Letter-style quotation with part and tooling tables for plastic injection molders.",
    requiredCustomFields: [
      { fieldName: "Log Number", fieldType: "string" },
      { fieldName: "Attention", fieldType: "string" },
    ],
  },
  {
    id: "erp-quotation",
    label: "ERP Quotation",
    description:
      "Epicor-style quotation with location branding and compact line-item blocks.",
  },
  {
    id: "service-quote",
    label: "Service Quote",
    description:
      "Proposal-style format with cover page, scope summary, and pricing table — designed for service and project-based quotes.",
  },
];
